<template>
    <ModalBase
        name="cargarEquiposAnterior"
        title="Cargar equipos de temporada anterior"
        @opened="onModalOpened"
    >
        <!-- Skeleton mientras carga -->
        <template v-if="loading">
            <div class="tw:space-y-3">
                <SkeletonItem height="tw:h-9" customClass="tw:w-44 tw:rounded-lg" />
                <div class="tw:space-y-2 tw:max-h-80 tw:overflow-y-auto">
                    <div
                        v-for="n in 5"
                        :key="'sk-' + n"
                        class="tw:flex tw:items-center tw:gap-3 tw:p-3 tw:border tw:border-gray-200 tw:rounded-lg"
                    >
                        <SkeletonItem height="tw:h-10" customClass="tw:w-10 tw:rounded shrink-0" />
                        <div class="tw:flex-1 tw:space-y-2">
                            <SkeletonItem height="tw:h-4" customClass="tw:w-3/5 tw:rounded" />
                            <SkeletonItem height="tw:h-3" customClass="tw:w-1/3 tw:rounded" />
                        </div>
                        <SkeletonItem height="tw:h-4" customClass="tw:w-8 tw:rounded-full shrink-0" />
                    </div>
                </div>
            </div>
        </template>

        <!-- Vacío -->
        <div v-else-if="equiposAnterior.length === 0" class="tw:text-center tw:text-gray-500 tw:py-4">
            No hay equipos disponibles en la temporada anterior.
        </div>

        <!-- Lista con filas clickeables -->
        <div v-else>
            <div class="tw:mb-4">
                <Button @click="toggleSeleccionarTodos">
                    {{ todosSeleccionados ? 'Deseleccionar todos' : 'Seleccionar todos' }}
                </Button>
            </div>

            <div class="tw:space-y-2 tw:max-h-80 tw:overflow-y-auto">
                <div
                    v-for="equipo in equiposAnterior"
                    :key="equipo.id"
                    @click="toggleEquipo(equipo.id)"
                    role="option"
                    :aria-selected="equipoSeleccionado(equipo.id)"
                    :class="[
                        'tw:flex tw:items-center tw:gap-3 tw:p-3 tw:border tw:rounded-lg tw:cursor-pointer tw:transition-all tw:select-none',
                        equipoSeleccionado(equipo.id) && !equipo.ya_existe
                            ? 'tw:border-blue-500 tw:bg-blue-50 tw:shadow-sm'
                            : 'tw:border-gray-200 tw:hover:border-blue-300 tw:hover:bg-gray-50',
                        equipo.ya_existe && 'tw:opacity-60 tw:cursor-not-allowed tw:bg-gray-50'
                    ]"
                >
                    <!-- Avatar / placeholder -->
                    <div
                        v-if="equipo.imagen"
                        class="tw:w-10 tw:h-10 tw:rounded tw:overflow-hidden tw:shrink-0 tw:bg-gray-100"
                    >
                        <img
                            :src="equipo.imagen"
                            class="tw:w-full tw:h-full tw:object-cover"
                            :alt="equipo.nombre"
                        />
                    </div>
                    <div
                        v-else
                        class="tw:w-10 tw:h-10 tw:rounded tw:bg-gray-100 tw:flex tw:items-center tw:justify-center tw:shrink-0"
                    >
                        <svg class="tw:w-5 tw:h-5 tw:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                    </div>

                    <!-- Nombre + estado -->
                    <div class="tw:flex-1 tw:min-w-0">
                        <div class="tw:font-medium tw:text-gray-900 tw:truncate">{{ equipo.nombre }}</div>
                        <div v-if="equipo.ya_existe" class="tw:text-xs tw:text-orange-600">
                            Ya existe en esta temporada
                        </div>
                    </div>

                    <!-- Indicador de selección -->
                    <div class="tw:shrink-0" @click.stop>
                        <!-- Ya existe: icono de advertencia -->
                        <div
                            v-if="equipo.ya_existe"
                            class="tw:w-6 tw:h-6 tw:rounded-full tw:bg-orange-100 tw:flex tw:items-center tw:justify-center"
                        >
                            <svg class="tw:w-4 tw:h-4 tw:text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <ToggleSwitch
                            v-else
                            size="sm"
                            :value="equipoSeleccionado(equipo.id)"
                            @input="toggleEquipo(equipo.id)"
                        />
                    </div>
                </div>
            </div>

            <div v-if="validationError" class="tw:text-red-600 tw:text-sm tw:mt-3">
                {{ validationError }}
            </div>
        </div>

        <template #footer>
            <Button
                :loading="submitting"
                :disabled="selectedIds.length === 0"
                @click="handleSubmit"
            >
                Cargar seleccionados
            </Button>
        </template>
    </ModalBase>
</template>

<script setup>
    import { ref, computed, getCurrentInstance } from 'vue';
    import ModalBase from '@/modules/admin/ui/ModalBase.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';
    import { useEquiposAnterior } from '@/composables/admin/bases/equipos/useEquiposAnterior';

    const props = defineProps({
        listaId: {
            type: [String, Number],
            required: true
        }
    });

    const emit = defineEmits(['imported']);

    const instance = getCurrentInstance();

    const {
        equiposAnterior,
        loading,
        submitting,
        fetchEquiposAnterior,
        cargarEquipos
    } = useEquiposAnterior();

    const selectedIds = ref([]);
    const validationError = ref('');

    const selectableEquipos = computed(() =>
        equiposAnterior.value.filter(e => !e.ya_existe)
    );

    const todosSeleccionados = computed(() => {
        const selectableIds = selectableEquipos.value.map(e => e.id);
        if (selectableIds.length === 0) return false;
        return selectableIds.every(id => selectedIds.value.includes(id));
    });

    function equipoSeleccionado(id) {
        return selectedIds.value.includes(id);
    }

    function toggleEquipo(id) {
        const equipo = equiposAnterior.value.find(e => e.id === id);
        if (!equipo || equipo.ya_existe) return;

        const idx = selectedIds.value.indexOf(id);
        if (idx === -1) {
            selectedIds.value.push(id);
        } else {
            selectedIds.value.splice(idx, 1);
        }
    }

    async function onModalOpened() {
        selectedIds.value = [];
        validationError.value = '';
        try {
            await fetchEquiposAnterior(props.listaId);
        } catch (error) {
            instance.proxy.$toast.error(
                error?.response?.data?.message || 'Error al cargar equipos de la temporada anterior'
            );
        }
    }

    function toggleSeleccionarTodos() {
        const selectableIds = selectableEquipos.value.map(e => e.id);
        if (todosSeleccionados.value) {
            selectedIds.value = selectedIds.value.filter(id => !selectableIds.includes(id));
        } else {
            selectedIds.value = [...new Set([...selectedIds.value, ...selectableIds])];
        }
    }

    async function handleSubmit() {
        validationError.value = '';
        if (selectedIds.value.length === 0) {
            validationError.value = 'Debe seleccionar al menos un equipo para cargar.';
            return;
        }
        try {
            await cargarEquipos(props.listaId, selectedIds.value);
            emit('imported');
            instance.proxy.$modal.hide('cargarEquiposAnterior');
        } catch (error) {
            instance.proxy.$toast.error(
                error?.response?.data?.message || 'Error al cargar los equipos'
            );
        }
    }
</script>
